import { useEffect, useMemo, useState } from 'react';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import GraphicEqOutlinedIcon from '@mui/icons-material/GraphicEqOutlined';
import GradingOutlinedIcon from '@mui/icons-material/GradingOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    LinearProgress,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import { Title, useNotify } from 'react-admin';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
    getMockTestAudioTraces,
    getMockTestReviewDetail,
    rescoreMockTestAttempt,
    type JsonValue,
    type MarkingResponseDto,
    type MockTestAudioTraceDto,
    type MockTestReviewAttemptDto,
    type MockTestReviewScoreMetricDto,
    type MockTestReviewTaskDto,
    type PublishedMockTestExamTaskDto,
} from '../admin/adminMockTestScoreReviewApi';
import { MediaPreview } from '../examTasks/MediaPreview';
import { formatApiDateTime } from '../admin/dateTimeUtils';

type DetailTabValue = 'score-details' | 'user-answer' | 'exam-task-details' | 'audio-traces';

type IndexedReviewTask = {
    category: string;
    examTaskType: string;
    sectionSequenceNumber: number;
    task: MockTestReviewTaskDto;
};

type ReviewTaskCategoryGroup = {
    category: string;
    groups: Array<{
        examTaskType: string;
        tasks: IndexedReviewTask[];
    }>;
};

type MetricCardProps = {
    label: string;
    value: string;
    helper?: string;
};

type DetailRowProps = {
    label: string;
    value: string;
};

const categoryOrder = ['Speaking', 'Writing', 'Reading', 'Listening'];

function formatDateTime(value: string | null | undefined): string {
    return formatApiDateTime(value);
}

function formatDuration(totalSeconds: number | null | undefined): string {
    if (totalSeconds === null || totalSeconds === undefined) return 'N/A';

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours}h ${minutes}m ${seconds}s`;
    }

    if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
    }

    return `${seconds}s`;
}

function formatBytes(totalBytes: number | null | undefined): string {
    if (totalBytes === null || totalBytes === undefined || !Number.isFinite(totalBytes)) {
        return 'N/A';
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = totalBytes;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }

    return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatBooleanLabel(value: boolean | null | undefined): string {
    if (value === null || value === undefined) return 'N/A';
    return value ? 'Yes' : 'No';
}

function formatNullableText(value: string | null | undefined, fallback = 'N/A'): string {
    return value && value.trim() ? value : fallback;
}

function buildMockTestDetailUrl(mockTestId: string): string {
    const url = new URL(window.location.href);
    url.hash = `#/mock-tests/${mockTestId}`;
    return url.toString();
}

function getUserAnswerText(task: MockTestReviewTaskDto | null): string {
    if (!task) {
        return 'No answer text was captured.';
    }

    const candidates = [
        task.userResponse?.answerText,
        task.userAnswer,
        task.scoredResponse?.transcript,
        task.scoredResponse?.scoreResult?.transcript,
        task.scoreResult?.transcript,
    ];

    for (const candidate of candidates) {
        if (candidate && candidate.trim()) {
            return candidate;
        }
    }

    return 'No answer text was captured.';
}

function formatScore(value: number | null | undefined, fallback = 'Not scored'): string {
    if (value === null || value === undefined) return fallback;
    return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.00$/, '');
}

function formatScoreOutOf(rawScore: number | null | undefined, maxScore: number | null | undefined): string {
    if (rawScore === null || rawScore === undefined || maxScore === null || maxScore === undefined) {
        return 'Not scored';
    }

    return `${formatScore(rawScore, '0')} / ${formatScore(maxScore, '0')}`;
}

function formatOutOf90(scorePercentage: number | null | undefined): string {
    return scorePercentage === null || scorePercentage === undefined ? 'Not scored' : `${scorePercentage} / 90`;
}

function hasJsonValue(value: JsonValue | null | undefined): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'number' || typeof value === 'boolean') return true;
    if (Array.isArray(value)) return value.length > 0;
    return Object.keys(value).length > 0;
}

function toPrettyJson(value: unknown): string {
    return JSON.stringify(value, null, 2);
}

function tryParseJson(value: string | null | undefined): unknown | null {
    if (!value || !value.trim()) {
        return null;
    }

    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
}

function buildTaskGroups(detail: MockTestReviewAttemptDto | null): ReviewTaskCategoryGroup[] {
    if (!detail) return [];

    const categoryMap = new Map<
        string,
        {
            order: number;
            groups: Map<string, IndexedReviewTask[]>;
        }
    >();

    const orderedSections = [...detail.sections].sort((left, right) => left.sequenceNumber - right.sequenceNumber);

    for (const section of orderedSections) {
        const category = section.sectionCategory || 'Unknown';
        const categoryEntry = categoryMap.get(category) ?? {
            order: categoryOrder.indexOf(category),
            groups: new Map<string, IndexedReviewTask[]>(),
        };

        for (const taskGroup of section.taskGroups) {
            const examTaskType = taskGroup.examTaskType || 'Unknown Exam Task Type';
            const tasks = categoryEntry.groups.get(examTaskType) ?? [];

            for (const task of [...taskGroup.tasks].sort((left, right) => left.sequenceNumber - right.sequenceNumber)) {
                tasks.push({
                    category,
                    examTaskType,
                    sectionSequenceNumber: section.sequenceNumber,
                    task,
                });
            }

            categoryEntry.groups.set(examTaskType, tasks);
        }

        categoryMap.set(category, categoryEntry);
    }

    return [...categoryMap.entries()]
        .sort((left, right) => {
            const leftOrder = left[1].order === -1 ? Number.MAX_SAFE_INTEGER : left[1].order;
            const rightOrder = right[1].order === -1 ? Number.MAX_SAFE_INTEGER : right[1].order;
            return leftOrder - rightOrder || left[0].localeCompare(right[0]);
        })
        .map(([category, groupRecord]) => ({
            category,
            groups: [...groupRecord.groups.entries()].map(([examTaskType, tasks]) => ({
                examTaskType,
                tasks: tasks.sort((left, right) => left.task.sequenceNumber - right.task.sequenceNumber),
            })),
        }));
}

function MetricCard({ label, value, helper }: MetricCardProps) {
    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 4,
                borderColor: 'rgba(148, 163, 184, 0.2)',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)',
            }}
        >
            <CardContent>
                <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: '0.12em' }}>
                    {label}
                </Typography>
                <Typography variant="h5" sx={{ color: '#0f172a', fontWeight: 800, lineBreak: 'anywhere' }}>
                    {value}
                </Typography>
                {helper ? (
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                        {helper}
                    </Typography>
                ) : null}
            </CardContent>
        </Card>
    );
}

function DetailRow({ label, value }: DetailRowProps) {
    return (
        <Box>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: '0.08em' }}>
                {label}
            </Typography>
            <Typography sx={{ color: '#0f172a', fontWeight: 600, overflowWrap: 'anywhere' }}>{value}</Typography>
        </Box>
    );
}

function JsonPanel({ title, value, emptyMessage }: { title: string; value: unknown; emptyMessage: string }) {
    if (!value || (typeof value === 'object' && !Array.isArray(value) && Object.keys(value as Record<string, unknown>).length === 0)) {
        return <Alert severity="info">{emptyMessage}</Alert>;
    }

    return (
        <Card variant="outlined" sx={{ borderRadius: 4 }}>
            <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
                    {title}
                </Typography>
                <Box
                    component="pre"
                    sx={{
                        m: 0,
                        p: 2,
                        borderRadius: 3,
                        backgroundColor: '#0f172a',
                        color: '#e2e8f0',
                        fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace',
                        fontSize: '0.84rem',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                    }}
                >
                    {toPrettyJson(value)}
                </Box>
            </CardContent>
        </Card>
    );
}

function ScoreMetricsSection({ metrics }: { metrics: MockTestReviewScoreMetricDto[] }) {
    if (metrics.length === 0) {
        return <Alert severity="info">No score metrics were returned for this exam task.</Alert>;
    }

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
            }}
        >
            {metrics.map((metric) => (
                <Card key={`${metric.metricKey}-${metric.metricLabel}`} variant="outlined" sx={{ borderRadius: 4 }}>
                    <CardContent>
                        <Stack spacing={1.25}>
                            <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>{metric.metricLabel || metric.metricKey}</Typography>
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                <Chip label={`Score ${formatScore(metric.score, '0')}`} size="small" />
                                <Chip label={`Out Of ${formatScore(metric.outOf, '0')}`} size="small" />
                                <Chip label={`${metric.scoreOutOf90}/90`} size="small" color="primary" />
                                {metric.isLast ? <Chip label="Final Metric" size="small" color="success" /> : null}
                            </Stack>
                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                                Key: {metric.metricKey || 'N/A'}
                            </Typography>
                        </Stack>
                    </CardContent>
                </Card>
            ))}
        </Box>
    );
}

function ExamTaskMetadataPanel({ examTask }: { examTask: PublishedMockTestExamTaskDto | null }) {
    if (!examTask) {
        return <Alert severity="info">No exam task details were returned for this item.</Alert>;
    }

    return (
        <Stack spacing={2}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                    gap: 2,
                }}
            >
                <DetailRow label="Display Title" value={formatNullableText(examTask.displayTitle)} />
                <DetailRow label="Exam Task Type" value={formatNullableText(examTask.examTaskType)} />
                <DetailRow label="Complexity Rank" value={formatNullableText(examTask.complexityRank)} />
                <DetailRow label="Premium Content" value={examTask.isPremiumContent ? 'Yes' : 'No'} />
                <DetailRow label="Active" value={examTask.isActive ? 'Yes' : 'No'} />
                <DetailRow label="Media Category" value={formatNullableText(examTask.mediaCategory)} />
                <DetailRow label="Created At" value={formatDateTime(examTask.createdAt)} />
                <DetailRow label="Updated At" value={formatDateTime(examTask.updatedAt)} />
            </Box>

            {examTask.promptBody ? (
                <Card variant="outlined" sx={{ borderRadius: 4 }}>
                    <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                            Prompt Body
                        </Typography>
                        <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{examTask.promptBody}</Typography>
                    </CardContent>
                </Card>
            ) : null}

            {examTask.answerChoices ? (
                <JsonPanel title="Answer Choices" value={examTask.answerChoices} emptyMessage="No answer choices were returned." />
            ) : null}

            {examTask.configSettings?.additional_options?.length ? (
                <Card variant="outlined" sx={{ borderRadius: 4 }}>
                    <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
                            Config Settings
                        </Typography>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {examTask.configSettings.additional_options.map((option) => (
                                <Chip key={option} label={option} />
                            ))}
                        </Stack>
                    </CardContent>
                </Card>
            ) : null}

            {examTask.mediaResourceUrl ? (
                <Card variant="outlined" sx={{ borderRadius: 4 }}>
                    <CardContent>
                        <Stack spacing={1.25} alignItems="flex-start">
                            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                Media Resource
                            </Typography>
                            <MediaPreview
                                url={examTask.mediaResourceUrl}
                                title="Media Resource"
                                width="100%"
                                height={500}
                                showUrl
                            />
                        </Stack>
                    </CardContent>
                </Card>
            ) : null}

            <Card variant="outlined" sx={{ borderRadius: 4 }}>
                <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
                        Segments
                    </Typography>
                    {examTask.segments.length === 0 ? (
                        <Alert severity="info">No segments were returned for this exam task.</Alert>
                    ) : (
                        <Stack spacing={1.5}>
                            {examTask.segments.map((segment, index) => (
                                <Card key={segment.examTaskSegmentId || `${segment.examTaskId}-${index}`} variant="outlined" sx={{ borderRadius: 3 }}>
                                    <CardContent>
                                        <Stack spacing={1.25}>
                                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                <Chip label={`Segment ${segment.sequenceNumber ?? index + 1}`} size="small" />
                                                {segment.correctIndex !== null ? <Chip label={`Correct Index ${segment.correctIndex}`} size="small" /> : null}
                                                {segment.isCorrectFlag !== null ? (
                                                    <Chip label={segment.isCorrectFlag ? 'Correct' : 'Incorrect'} size="small" color={segment.isCorrectFlag ? 'success' : 'default'} />
                                                ) : null}
                                                {!segment.isEnabled ? <Chip label="Disabled" size="small" color="warning" /> : null}
                                            </Stack>
                                            <Typography variant="body2">
                                                <strong>Prefix:</strong> {formatNullableText(segment.prefixText)}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Suffix:</strong> {formatNullableText(segment.suffixText)}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Option List:</strong> {formatNullableText(segment.optionList)}
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    )}
                </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 4 }}>
                <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
                        Voices
                    </Typography>
                    {examTask.voices.length === 0 ? (
                        <Alert severity="info">No voices were returned for this exam task.</Alert>
                    ) : (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                gap: 2,
                            }}
                        >
                            {examTask.voices.map((voice) => (
                                <Card key={voice.examTaskMediaVoiceId} variant="outlined" sx={{ borderRadius: 3 }}>
                                    <CardContent>
                                        <Stack spacing={0.75}>
                                            <Typography sx={{ fontWeight: 800 }}>{voice.voiceLabel || 'Voice'}</Typography>
                                            <Typography variant="body2">Language: {formatNullableText(voice.languageTag)}</Typography>
                                            <Typography variant="body2">Gender: {formatNullableText(voice.genderCode)}</Typography>
                                            <Typography variant="body2">Duration: {formatDuration(Math.round(voice.durationSeconds))}</Typography>
                                            <Typography variant="body2" sx={{ color: '#64748b', overflowWrap: 'anywhere' }}>
                                                File: {formatNullableText(voice.storageFilename)}
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Stack>
    );
}

function AudioTracePanel({ traces }: { traces: MockTestAudioTraceDto[] }) {
    if (traces.length === 0) {
        return <Alert severity="info">No audio trace rows were returned for this attempt.</Alert>;
    }

    return (
        <Stack spacing={2}>
            {traces.map((trace, index) => {
                const scoreSummaryJson = tryParseJson(trace.scorePayloadSummary);

                return (
                    <Card key={trace.requestId} variant="outlined" sx={{ borderRadius: 4 }}>
                        <CardContent>
                            <Stack spacing={2}>
                                <Stack spacing={1}>
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                        <Chip label={`Trace ${index + 1}`} size="small" />
                                        <Chip label={trace.examTaskCategory} size="small" />
                                        <Chip label={trace.examTaskType} size="small" />
                                        <Chip label={trace.questionNo} size="small" color="primary" />
                                    </Stack>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                        {trace.requestId}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                                        Item {trace.mockTestAttemptItemId} · Task {trace.examTaskId}
                                    </Typography>
                                </Stack>

                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
                                        gap: 2,
                                    }}
                                >
                                    <Card variant="outlined" sx={{ borderRadius: 3 }}>
                                        <CardContent>
                                            <Stack spacing={1}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Client Evidence</Typography>
                                                <DetailRow label="Reported Recording" value={formatBooleanLabel(trace.clientReportedRecording)} />
                                                <DetailRow label="Recording Duration" value={formatDuration(Math.round((trace.clientRecordingDurationMs ?? 0) / 1000))} />
                                                <DetailRow label="Client Attached File" value={formatBooleanLabel(trace.clientAttachedFile)} />
                                                <DetailRow label="Client File Name" value={formatNullableText(trace.clientAttachedFileName)} />
                                                <DetailRow label="Client File Size" value={formatBytes(trace.clientAttachedFileSizeBytes)} />
                                                <DetailRow label="Client Content Type" value={formatNullableText(trace.clientAudioContentType)} />
                                                <DetailRow label="Client Last Action" value={formatNullableText(trace.clientLastAction)} />
                                            </Stack>
                                        </CardContent>
                                    </Card>

                                    <Card variant="outlined" sx={{ borderRadius: 3 }}>
                                        <CardContent>
                                            <Stack spacing={1}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>API and Storage</Typography>
                                                <DetailRow label="API Received At" value={formatDateTime(trace.apiReceivedAt)} />
                                                <DetailRow label="API Received File" value={formatBooleanLabel(trace.apiReceivedFile)} />
                                                <DetailRow label="API File Name" value={formatNullableText(trace.apiReceivedFileName)} />
                                                <DetailRow label="API File Size" value={formatBytes(trace.apiReceivedFileSizeBytes)} />
                                                <DetailRow label="API Content Type" value={formatNullableText(trace.apiReceivedAudioContentType)} />
                                                <DetailRow label="API Audio Duration" value={formatNullableText(trace.apiClientAudioDurationSeconds === null ? null : `${trace.apiClientAudioDurationSeconds.toFixed(2)}s`)} />
                                                <DetailRow label="Source Upload Status" value={formatNullableText(trace.sourceAudioUploadStatus)} />
                                                <DetailRow label="Source Path" value={formatNullableText(trace.sourceAudioPath)} />
                                                <DetailRow label="Source Uploaded At" value={formatDateTime(trace.sourceAudioUploadedAt)} />
                                                <DetailRow label="Answer Audio URL" value={formatNullableText(trace.answerAudioUrl)} />
                                            </Stack>
                                        </CardContent>
                                    </Card>

                                    <Card variant="outlined" sx={{ borderRadius: 3 }}>
                                        <CardContent>
                                            <Stack spacing={1}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Processing</Typography>
                                                <DetailRow label="Queue Published At" value={formatDateTime(trace.queuePublishedAt)} />
                                                <DetailRow label="Queued For Persistence" value={formatBooleanLabel(trace.queuedForPersistence)} />
                                                <DetailRow label="Queued For Audio" value={formatBooleanLabel(trace.queuedForAudioProcessing)} />
                                                <DetailRow label="Queued For Scoring" value={formatBooleanLabel(trace.queuedForScoring)} />
                                                <DetailRow label="Persistence Status" value={formatNullableText(trace.persistenceStatus)} />
                                                <DetailRow label="Audio Status" value={formatNullableText(trace.audioConsumerStatus)} />
                                                <DetailRow label="Normalized Audio" value={formatNullableText(trace.normalizedAudioPath)} />
                                                <DetailRow label="Scoring Status" value={formatNullableText(trace.scoringStatus)} />
                                                <DetailRow label="Speech Status" value={formatNullableText(trace.speechAnalysisStatus)} />
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Box>

                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                        gap: 2,
                                    }}
                                >
                                    <Card variant="outlined" sx={{ borderRadius: 3 }}>
                                        <CardContent>
                                            <Stack spacing={1}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Speech Output</Typography>
                                                <DetailRow label="Transcript Length" value={trace.speechTranscriptLength === null ? 'N/A' : String(trace.speechTranscriptLength)} />
                                                <DetailRow label="Token Count" value={trace.speechTokenCount === null ? 'N/A' : String(trace.speechTokenCount)} />
                                                <DetailRow label="Pause Count" value={trace.speechPauseEventCount === null ? 'N/A' : String(trace.speechPauseEventCount)} />
                                                <DetailRow label="Speech Duration" value={trace.speechAudioDurationSeconds === null ? 'N/A' : `${trace.speechAudioDurationSeconds.toFixed(2)}s`} />
                                                <DetailRow label="Transcript Preview" value={formatNullableText(trace.speechTranscriptPreview)} />
                                                {trace.speechTranscript ? <JsonPanel title="Transcript" value={trace.speechTranscript} emptyMessage="No transcript was returned." /> : null}
                                            </Stack>
                                        </CardContent>
                                    </Card>

                                    <Card variant="outlined" sx={{ borderRadius: 3 }}>
                                        <CardContent>
                                            <Stack spacing={1}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Score Output</Typography>
                                                <DetailRow label="Scoring Used Audio" value={formatBooleanLabel(trace.scoringUsedAudio)} />
                                                <DetailRow label="No Audio Reason" value={formatNullableText(trace.scoringNoAudioReason)} />
                                                <DetailRow label="Raw Score" value={trace.scoreRaw === null ? 'N/A' : String(trace.scoreRaw)} />
                                                <DetailRow label="Max Score" value={trace.scoreMax === null ? 'N/A' : String(trace.scoreMax)} />
                                                <DetailRow label="Scoring Error" value={formatNullableText(trace.scoringErrorCode ?? trace.scoringErrorMessage)} />
                                                <DetailRow label="Audio Error" value={formatNullableText(trace.audioConsumerErrorCode ?? trace.audioConsumerErrorMessage)} />
                                                <DetailRow label="Persistence Error" value={formatNullableText(trace.persistenceErrorCode ?? trace.persistenceErrorMessage)} />
                                                <DetailRow label="Score Summary" value={formatNullableText(trace.scoreSummary)} />
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Box>

                                {scoreSummaryJson ? (
                                    <JsonPanel
                                        title="Score Payload Summary"
                                        value={scoreSummaryJson}
                                        emptyMessage="No JSON score payload summary was returned for this audio trace."
                                    />
                                ) : null}
                            </Stack>
                        </CardContent>
                    </Card>
                );
            })}
        </Stack>
    );
}

function scoreResultValue(scoreResult: MarkingResponseDto | null): unknown {
    if (!scoreResult) return null;

    return {
        ...scoreResult,
        hasOralFluencyReview: Boolean(scoreResult.oralFluencyReview),
    };
}

export function MockTestScoreReviewDetailPage() {
    const { mockTestAttemptId } = useParams<{ mockTestAttemptId: string }>();
    const navigate = useNavigate();
    const notify = useNotify();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [rescoreLoading, setRescoreLoading] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);
    const [error, setError] = useState('');
    const [detail, setDetail] = useState<MockTestReviewAttemptDto | null>(null);
    const [audioTraces, setAudioTraces] = useState<MockTestAudioTraceDto[]>([]);
    const [audioTracesError, setAudioTracesError] = useState('');
    const [selectedTaskId, setSelectedTaskId] = useState<string>('');
    const [activeTab, setActiveTab] = useState<DetailTabValue>('score-details');
    const rescoreUserId = useMemo(() => {
        const queryUserId = searchParams.get('userId')?.trim();
        if (queryUserId) {
            return queryUserId;
        }

        if (!mockTestAttemptId) {
            return '';
        }

        return sessionStorage.getItem(`mock-test-score-review:user:${mockTestAttemptId}`)?.trim() ?? '';
    }, [mockTestAttemptId, searchParams]);

    useEffect(() => {
        if (!mockTestAttemptId) {
            setDetail(null);
            setError('Mock test attempt id is missing.');
            return;
        }

        let active = true;

        setLoading(true);
        setError('');
        setAudioTracesError('');

        void Promise.allSettled([
            getMockTestReviewDetail({ mockTestAttemptId }),
            getMockTestAudioTraces({ mockTestAttemptId }),
        ])
            .then(([detailResult, traceResult]) => {
                if (!active) return;

                if (detailResult.status === 'fulfilled') {
                    setDetail(detailResult.value.data);
                } else {
                    setDetail(null);
                    setError(detailResult.reason instanceof Error ? detailResult.reason.message : 'Unable to load mock test review detail');
                }

                if (traceResult.status === 'fulfilled') {
                    setAudioTraces(traceResult.value.data);
                } else {
                    setAudioTraces([]);
                    setAudioTracesError(traceResult.reason instanceof Error ? traceResult.reason.message : 'Unable to load audio traces');
                }
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [mockTestAttemptId, reloadToken]);

    const groupedTasks = useMemo(() => buildTaskGroups(detail), [detail]);
    const indexedTasks = useMemo(
        () => groupedTasks.flatMap((category) => category.groups.flatMap((group) => group.tasks)),
        [groupedTasks],
    );
    const selectedTaskEntry = useMemo(
        () => indexedTasks.find((entry) => entry.task.mockTestAttemptItemId === selectedTaskId) ?? indexedTasks[0] ?? null,
        [indexedTasks, selectedTaskId],
    );
    const selectedTask = selectedTaskEntry?.task ?? null;
    const primaryScoreMetrics = selectedTask?.scoreMetrics.length
        ? selectedTask.scoreMetrics
        : selectedTask?.scoredResponse?.scoreMetrics ?? [];
    const scoreResult = selectedTask?.scoredResponse?.scoreResult ?? selectedTask?.scoreResult ?? null;

    useEffect(() => {
        if (!selectedTaskEntry) {
            if (selectedTaskId) {
                setSelectedTaskId('');
            }
            return;
        }

        if (selectedTaskId !== selectedTaskEntry.task.mockTestAttemptItemId) {
            setSelectedTaskId(selectedTaskEntry.task.mockTestAttemptItemId);
        }
    }, [selectedTaskEntry, selectedTaskId]);

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }

        navigate('/mock-test-score-review');
    };

    const handleRescoreAttempt = async () => {
        if (!detail || !mockTestAttemptId) {
            notify('Mock test review detail is not loaded.', { type: 'warning' });
            return;
        }

        if (!rescoreUserId) {
            notify('User id is missing for this review attempt. Open it from the score review list and try again.', {
                type: 'warning',
            });
            return;
        }

        const confirmed = window.confirm('Rescore this mock test attempt? This reruns scoring for the current attempt.');
        if (!confirmed) {
            return;
        }

        setRescoreLoading(true);
        try {
            const result = await rescoreMockTestAttempt({
                userId: rescoreUserId,
                mockTestId: detail.mockTestId,
                mockTestAttemptId: detail.mockTestAttemptId,
            });

            notify(
                result.scoreResult.overallScore === null
                    ? 'Mock test attempt rescored.'
                    : `Mock test attempt rescored. Overall score: ${formatScore(result.scoreResult.overallScore, '0')}.`,
                { type: 'info' },
            );
            setReloadToken((current) => current + 1);
        } catch (rescoreError: unknown) {
            notify(rescoreError instanceof Error ? rescoreError.message : 'Unable to rescore mock test attempt', {
                type: 'error',
            });
        } finally {
            setRescoreLoading(false);
        }
    };

    return (
        <>
            <Title title={detail ? `${detail.mockTestTitle} Review` : 'Mock Test Review Detail'} />
            <Stack spacing={3} sx={{ p: { xs: 2, md: 3 }, maxWidth: '100%' }}>
                <Box>
                    <Button
                        variant="text"
                        startIcon={<ArrowBackOutlinedIcon />}
                        onClick={handleBack}
                        sx={{ mb: 1, pl: 0, textTransform: 'none' }}
                    >
                        Back to Mock Test Score Review
                    </Button>
                </Box>

                <Card
                    sx={{
                        borderRadius: 5,
                        overflow: 'hidden',
                        color: '#e2e8f0',
                        background:
                            'radial-gradient(circle at top left, rgba(56, 189, 248, 0.32) 0%, rgba(15, 23, 42, 0) 36%), linear-gradient(135deg, #0f172a 0%, #172554 52%, #111827 100%)',
                        boxShadow: '0 28px 70px rgba(15, 23, 42, 0.28)',
                    }}
                >
                    {loading ? <LinearProgress color="info" /> : null}
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Stack spacing={3}>
                            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between">
                                <Box>
                                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                                        <AssessmentOutlinedIcon sx={{ color: '#67e8f9' }} />
                                        <Typography variant="overline" sx={{ letterSpacing: '0.16em', fontWeight: 800, color: '#67e8f9' }}>
                                            Review Detail
                                        </Typography>
                                    </Stack>
                                    <Typography variant="h4" sx={{ fontWeight: 800, maxWidth: 900 }}>
                                        {detail ? detail.mockTestTitle : 'Mock Test Review Detail'}
                                    </Typography>
                                    <Typography sx={{ mt: 1.25, maxWidth: 860, color: 'rgba(226, 232, 240, 0.82)' }}>
                                        Inspect section-by-section exam task review detail, switch between item numbers in exam order, and inspect score data, user answers, and exam task metadata on one screen.
                                    </Typography>
                                </Box>

                                {detail ? (
                                    <Stack spacing={1.5} alignItems={{ xs: 'flex-start', lg: 'flex-end' }}>
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
                                            {detail.examName ? <Chip label={detail.examName} sx={{ bgcolor: 'rgba(103, 232, 249, 0.16)', color: '#e0f2fe' }} /> : null}
                                            {detail.mockTestKind ? <Chip label={detail.mockTestKind} sx={{ bgcolor: 'rgba(148, 163, 184, 0.18)', color: '#e2e8f0' }} /> : null}
                                            {detail.status ? <Chip label={detail.status} sx={{ bgcolor: 'rgba(52, 211, 153, 0.16)', color: '#d1fae5' }} /> : null}
                                        </Stack>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                                            {detail.mockTestId ? (
                                                <Button
                                                    variant="outlined"
                                                    component="a"
                                                    href={buildMockTestDetailUrl(detail.mockTestId)}
                                                    sx={{
                                                        borderColor: 'rgba(103, 232, 249, 0.45)',
                                                        color: '#67e8f9',
                                                        fontWeight: 800,
                                                    }}
                                                >
                                                    Open Mock Test
                                                </Button>
                                            ) : null}
                                            <Button
                                                variant="contained"
                                                startIcon={<RefreshOutlinedIcon />}
                                                onClick={handleRescoreAttempt}
                                                disabled={loading || rescoreLoading || !rescoreUserId}
                                                sx={{
                                                    background: 'linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%)',
                                                    color: '#082f49',
                                                    fontWeight: 800,
                                                }}
                                            >
                                                {rescoreLoading ? 'Rescoring...' : 'Rescore Attempt'}
                                            </Button>
                                        </Stack>
                                    </Stack>
                                ) : null}
                            </Stack>

                            {detail ? (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                                        gap: 2,
                                    }}
                                >
                                    <MetricCard label="Attempt ID" value={detail.mockTestAttemptId} />
                                    <MetricCard label="Attempted" value={formatDateTime(detail.attemptedAt)} />
                                    <MetricCard label="Overall Score" value={formatScore(detail.overallScore)} helper={formatOutOf90(detail.overallScore)} />
                                    <MetricCard label="Total Time" value={formatDuration(detail.totalTimeSpentSeconds)} helper={`${indexedTasks.length} exam tasks`} />
                                </Box>
                            ) : null}
                        </Stack>
                    </CardContent>
                </Card>

                {error ? <Alert severity="error">{error}</Alert> : null}

                {!loading && !error && detail ? (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', lg: 'minmax(300px, 360px) minmax(0, 1fr)' },
                            gap: 3,
                            alignItems: 'start',
                        }}
                    >
                        <Card
                            sx={{
                                borderRadius: 4,
                                border: '1px solid rgba(148, 163, 184, 0.18)',
                                position: { lg: 'sticky' },
                                top: { lg: 24 },
                            }}
                        >
                            <CardContent>
                                <Stack spacing={2.5}>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                            Review Navigator
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                                            Choose an exam task number in the same sequence order it appeared in the mock test.
                                        </Typography>
                                    </Box>

                                    {groupedTasks.length === 0 ? (
                                        <Alert severity="info">No review tasks were returned for this attempt.</Alert>
                                    ) : (
                                        groupedTasks.map((category) => (
                                            <Stack key={category.category} spacing={1.5}>
                                                <Typography
                                                    variant="overline"
                                                    sx={{ color: '#0f172a', fontWeight: 900, letterSpacing: '0.12em' }}
                                                >
                                                    {category.category}
                                                </Typography>

                                                {category.groups.map((group) => (
                                                    <Stack key={`${category.category}-${group.examTaskType}`} spacing={1}>
                                                        <Typography sx={{ fontWeight: 700, color: '#334155' }}>{group.examTaskType}</Typography>
                                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                            {group.tasks.map((entry) => {
                                                                const isSelected = entry.task.mockTestAttemptItemId === selectedTask?.mockTestAttemptItemId;

                                                                return (
                                                                    <Chip
                                                                        key={entry.task.mockTestAttemptItemId}
                                                                        clickable
                                                                        onClick={() => {
                                                                            setSelectedTaskId(entry.task.mockTestAttemptItemId);
                                                                        }}
                                                                        label={entry.task.sequenceNumber}
                                                                        color={isSelected ? 'primary' : 'default'}
                                                                        variant={isSelected ? 'filled' : 'outlined'}
                                                                        sx={{
                                                                            borderRadius: 999,
                                                                            minWidth: 42,
                                                                            '& .MuiChip-label': { fontWeight: 800 },
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                        </Stack>
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        ))
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>

                        <Card sx={{ borderRadius: 4, border: '1px solid rgba(148, 163, 184, 0.18)', overflow: 'hidden' }}>
                            <CardContent sx={{ p: 0 }}>
                                {!selectedTask || !selectedTaskEntry ? (
                                    <Alert severity="info" sx={{ m: 2 }}>
                                        Select an exam task from the left side to inspect the review detail.
                                    </Alert>
                                ) : (
                                    <>
                                        <Box
                                            sx={{
                                                px: { xs: 2, md: 3 },
                                                py: { xs: 2.5, md: 3 },
                                                background:
                                                    'linear-gradient(180deg, rgba(239,246,255,0.98) 0%, rgba(248,250,252,0.98) 100%)',
                                            }}
                                        >
                                            <Stack spacing={2}>
                                                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between">
                                                    <Box>
                                                        <Typography variant="overline" sx={{ color: '#0369a1', fontWeight: 900, letterSpacing: '0.14em' }}>
                                                            {selectedTaskEntry.category} • {selectedTaskEntry.examTaskType}
                                                        </Typography>
                                                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', mt: 0.5 }}>
                                                            Exam Task {selectedTask.sequenceNumber}
                                                        </Typography>
                                                        <Typography sx={{ color: '#475569', mt: 1, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                                            {formatNullableText(selectedTask.questionText, 'No question text returned.')}
                                                        </Typography>
                                                    </Box>

                                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
                                                        <Chip label={`Status ${formatNullableText(selectedTask.status)}`} />
                                                        <Chip label={`Time ${formatDuration(selectedTask.timeSpentSeconds)}`} />
                                                        <Chip label={`Score ${formatOutOf90(selectedTask.scorePercentage)}`} color="primary" />
                                                    </Stack>
                                                </Stack>

                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                                                        gap: 2,
                                                    }}
                                                >
                                                    <MetricCard label="Raw Score" value={formatScoreOutOf(selectedTask.rawScore, selectedTask.maxScore)} />
                                                    <MetricCard label="Out Of 90" value={formatOutOf90(selectedTask.scorePercentage)} />
                                                    <MetricCard label="Started" value={formatDateTime(selectedTask.startedAt)} />
                                                    <MetricCard label="Scored" value={formatDateTime(selectedTask.scoredAt)} />
                                                </Box>
                                            </Stack>
                                        </Box>

                                        <Divider />

                                        <Box sx={{ px: { xs: 2, md: 3 }, pt: 2 }}>
                                            <Tabs
                                                value={activeTab}
                                                onChange={(_event, nextValue: DetailTabValue) => setActiveTab(nextValue)}
                                                variant="scrollable"
                                                scrollButtons="auto"
                                                allowScrollButtonsMobile
                                                sx={{
                                                    '& .MuiTab-root': {
                                                        textTransform: 'none',
                                                        fontWeight: 800,
                                                        minHeight: 48,
                                                    },
                                                }}
                                            >
                                                <Tab value="score-details" label="Score Details" icon={<GradingOutlinedIcon />} iconPosition="start" />
                                                <Tab value="user-answer" label="User Answer" icon={<DescriptionOutlinedIcon />} iconPosition="start" />
                                                <Tab value="exam-task-details" label="Exam Task Details" icon={<AssessmentOutlinedIcon />} iconPosition="start" />
                                                <Tab value="audio-traces" label="Audio Traces" icon={<GraphicEqOutlinedIcon />} iconPosition="start" />
                                            </Tabs>
                                        </Box>

                                        <Divider />

                                        <Stack spacing={2.5} sx={{ p: { xs: 2, md: 3 } }}>
                                            {activeTab === 'score-details' ? (
                                                <>
                                                    <Box
                                                        sx={{
                                                            display: 'grid',
                                                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                                            gap: 2,
                                                        }}
                                                    >
                                                        <Card variant="outlined" sx={{ borderRadius: 4 }}>
                                                            <CardContent>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
                                                                    Score Summary
                                                                </Typography>
                                                                <Stack spacing={1.25}>
                                                                    <DetailRow label="Raw Score" value={formatScoreOutOf(selectedTask.rawScore, selectedTask.maxScore)} />
                                                                    <DetailRow label="Score Out Of 90" value={formatOutOf90(selectedTask.scorePercentage)} />
                                                                    <DetailRow label="Remaining Seconds" value={selectedTask.remainingSeconds === null ? 'N/A' : String(selectedTask.remainingSeconds)} />
                                                                    <DetailRow label="Task Status" value={formatNullableText(selectedTask.status)} />
                                                                </Stack>
                                                            </CardContent>
                                                        </Card>

                                                        <Card variant="outlined" sx={{ borderRadius: 4 }}>
                                                            <CardContent>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
                                                                    Review Timeline
                                                                </Typography>
                                                                <Stack spacing={1.25}>
                                                                    <DetailRow label="Started At" value={formatDateTime(selectedTask.startedAt)} />
                                                                    <DetailRow label="Answered At" value={formatDateTime(selectedTask.answeredAt)} />
                                                                    <DetailRow label="Submitted At" value={formatDateTime(selectedTask.submittedAt)} />
                                                                    <DetailRow label="Scored At" value={formatDateTime(selectedTask.scoredAt)} />
                                                                </Stack>
                                                            </CardContent>
                                                        </Card>
                                                    </Box>

                                                    <Card variant="outlined" sx={{ borderRadius: 4 }}>
                                                        <CardContent>
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
                                                                Score Metrics
                                                            </Typography>
                                                            <ScoreMetricsSection metrics={primaryScoreMetrics} />
                                                        </CardContent>
                                                    </Card>

                                                    <JsonPanel
                                                        title="Score Result Payload"
                                                        value={scoreResultValue(scoreResult)}
                                                        emptyMessage="No score result payload was returned for this exam task."
                                                    />
                                                </>
                                            ) : null}

                                            {activeTab === 'user-answer' ? (
                                                <>
                                                    <Card variant="outlined" sx={{ borderRadius: 4 }}>
                                                        <CardContent>
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
                                                                User Answer
                                                            </Typography>
                                                            <Stack spacing={1.5}>
                                                                <DetailRow label="Answer Text" value={getUserAnswerText(selectedTask)} />

                                                                {selectedTask.userResponse?.answerAudioUrl || selectedTask.answerAudioUrl ? (
                                                                    <Stack spacing={1.25} alignItems="flex-start">
                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={{ color: '#64748b', fontWeight: 800, letterSpacing: '0.08em' }}
                                                                        >
                                                                            Answer Media
                                                                        </Typography>
                                                                        <MediaPreview
                                                                            url={selectedTask.userResponse?.answerAudioUrl ?? selectedTask.answerAudioUrl ?? ''}
                                                                            title="Answer Media"
                                                                            width="100%"
                                                                            showUrl
                                                                        />
                                                                    </Stack>
                                                                ) : null}
                                                            </Stack>
                                                        </CardContent>
                                                    </Card>

                                                    {hasJsonValue(selectedTask.userResponse?.responsePayload) ? (
                                                        <JsonPanel
                                                            title="User Response Payload"
                                                            value={selectedTask.userResponse?.responsePayload}
                                                            emptyMessage="No response payload was returned for this exam task."
                                                        />
                                                    ) : (
                                                        <Alert severity="info">No response payload was returned for this exam task.</Alert>
                                                    )}
                                                </>
                                            ) : null}

                                            {activeTab === 'exam-task-details' ? (
                                                <>
                                                    <Card variant="outlined" sx={{ borderRadius: 4 }}>
                                                        <CardContent>
                                                            <Stack spacing={1.25}>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                                                    Attempt Item Detail
                                                                </Typography>
                                                                <Box
                                                                    sx={{
                                                                        display: 'grid',
                                                                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                                                        gap: 2,
                                                                    }}
                                                                >
                                                                    <DetailRow label="Attempt Item Id" value={selectedTask.mockTestAttemptItemId} />
                                                                    <DetailRow label="Mock Test Item Id" value={selectedTask.mockTestItemId} />
                                                                    <DetailRow label="Exam Task Id" value={selectedTask.examTaskId} />
                                                                    <DetailRow label="Section Order" value={String(selectedTaskEntry.sectionSequenceNumber)} />
                                                                </Box>
                                                            </Stack>
                                                        </CardContent>
                                                    </Card>

                                                    <ExamTaskMetadataPanel examTask={selectedTask.examTask} />
                                                </>
                                            ) : null}

                                            {activeTab === 'audio-traces' ? (
                                                <>
                                                    {audioTracesError ? <Alert severity="warning">{audioTracesError}</Alert> : null}
                                                    <Card variant="outlined" sx={{ borderRadius: 4 }}>
                                                        <CardContent>
                                                            <Stack spacing={1.25}>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                                                    Trace Summary
                                                                </Typography>
                                                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                                    <Chip label={`Trace rows ${audioTraces.length}`} />
                                                                    <Chip label={`Attempt ${detail.mockTestAttemptId}`} color="primary" />
                                                                </Stack>
                                                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                                                    Each row captures the end-to-end audio lifecycle for one submission request: what the client reported, what the API received, where the source audio was stored, and how persistence, audio conversion, and scoring progressed.
                                                                </Typography>
                                                            </Stack>
                                                        </CardContent>
                                                    </Card>

                                                    <AudioTracePanel traces={audioTraces} />
                                                </>
                                            ) : null}
                                        </Stack>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Box>
                ) : null}
            </Stack>
        </>
    );
}